import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Radio } from '../ui/Radio.js';
import { Toggle } from '../ui/Toggle.js';
import { Badge } from '../ui/Badge.js';
import { Modal, MODAL_EXIT_MS } from '../ui/Modal.js';
import type { LeadPipelineInput, ScrapeZone } from '../../hooks/useSettings.js';

export const SCRAPE_CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'restaurant',    label: 'Restaurant' },
  { value: 'dental_clinic', label: 'Dental Clinic' },
  { value: 'gym',           label: 'Gym' },
  { value: 'beauty_salon',  label: 'Beauty Salon' },
  { value: 'lawyer',        label: 'Lawyer' },
  { value: 'real_estate',   label: 'Real Estate' },
  { value: 'auto_repair',   label: 'Auto Repair' },
  { value: 'plumber',       label: 'Plumber' },
  { value: 'electrician',   label: 'Electrician' },
  { value: 'contractor',    label: 'Contractor' },
];

const STEP_HINT_KEY: Record<string, string> = {
  autoScrape:   'scrapeHint',
  autoEnrich:   'enrichHint',
  autoAnalysis: 'analysisHint',
  autoDraft:    'draftHint',
  autoSend:     'sendHint',
};

interface Props {
  title?: string;
  onSave: (data: LeadPipelineInput) => void;
  onCancel: () => void;
  saving?: boolean;
  error?: string | null;
}

function ModalRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: 'var(--evo-fg-3)' }}>{label}</span>
        {hint && <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)', marginLeft: '8px' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function PipelineEditorModal({ title, onSave, onCancel, saving, error }: Props) {
  const { t } = useTranslation();

  const [name, setName]             = useState('');
  const [enabled, setEnabled]       = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [zones, setZones]           = useState<ScrapeZone[]>([]);
  const [zoneInput, setZoneInput]   = useState('');
  const [capPerRun, setCapPerRun]   = useState(10);
  const [cronHour, setCronHour]     = useState(8);
  const [cronMinute, setCronMinute] = useState(0);
  const [intent, setIntent]         = useState<'audit' | 'demo' | 'any'>('any');
  const [autoScrape, setAutoScrapeRaw]     = useState(true);
  const [autoEnrich, setAutoEnrichRaw]     = useState(true);
  const [autoAnalysis, setAutoAnalysisRaw] = useState(true);
  const [autoDraft, setAutoDraftRaw]       = useState(false);
  const [autoSend, setAutoSend]            = useState(false);

  const [isOpen, setIsOpen] = useState(true);
  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => onCancel(), MODAL_EXIT_MS + 20);
  };

  function setAutoScrape(v: boolean) {
    setAutoScrapeRaw(v);
    if (!v) { setAutoEnrichRaw(false); setAutoAnalysisRaw(false); setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoEnrich(v: boolean) {
    setAutoEnrichRaw(v);
    if (!v) { setAutoAnalysisRaw(false); setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoAnalysis(v: boolean) {
    setAutoAnalysisRaw(v);
    if (!v) { setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoDraft(v: boolean) {
    setAutoDraftRaw(v);
    if (!v) setAutoSend(false);
  }

  function addZone() {
    const trimmed = zoneInput.trim();
    if (!trimmed) return;
    const parts = trimmed.split(',').map(p => p.trim());
    setZones(z => [...z, { city: parts[0] ?? trimmed, state: parts[1] ?? '' }]);
    setZoneInput('');
  }

  function handleSave() {
    onSave({ name, enabled, categories, zones, capPerRun, cronHour, cronMinute, intent, autoScrape, autoEnrich, autoAnalysis, autoDraft, autoSend });
  }

  const hourOptions   = Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') + ':00' }));
  const minuteOptions = [0, 15, 30, 45].map(m => ({ value: m, label: String(m).padStart(2, '0') }));

  const steps = [
    { key: 'autoScrape',   value: autoScrape,   onChange: setAutoScrape,   disabled: false },
    { key: 'autoEnrich',   value: autoEnrich,   onChange: setAutoEnrich,   disabled: !autoScrape },
    { key: 'autoAnalysis', value: autoAnalysis, onChange: setAutoAnalysis, disabled: !autoEnrich },
    { key: 'autoDraft',    value: autoDraft,    onChange: setAutoDraft,    disabled: !autoAnalysis },
    { key: 'autoSend',     value: autoSend,     onChange: setAutoSend,     disabled: !autoDraft },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={title ?? t('settings.pipelines.addPipeline')}
      footer={
        <>
          <Button variant="outline" tone="muted" size="sm" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="solid"
            tone="cyan"
            size="sm"
            loading={!!saving}
            disabled={saving || !name.trim()}
            onClick={handleSave}
          >
            {t('settings.pipelines.createBtn')}
          </Button>
        </>
      }
    >
      {/* Name + enabled */}
      <div className="flex items-center gap-3 mb-4">
        <Input
          type="text"
          size="sm"
          fullWidth
          weight="bold"
          value={name}
          onChange={v => setName(v)}
          placeholder={t('settings.pipelines.namePlaceholder')}
        />
        <Toggle checked={enabled} onChange={setEnabled} label={t('settings.pipelines.enabledLabel')} size="sm" />
      </div>

      <ModalRow label={t('settings.scrapeSchedule.categoriesLabel')}>
        <Dropdown<string>
          multi
          value={categories}
          options={SCRAPE_CATEGORY_OPTIONS}
          onChange={setCategories}
          placeholder={t('settings.scrapeSchedule.categoriesPlaceholder')}
          size="sm"
        />
      </ModalRow>

      <ModalRow label={t('settings.scrapeSchedule.zonesLabel')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {zones.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
              {zones.map((z, i) => (
                <Badge
                  key={i} variant="soft" tone="cyan" size="sm"
                  rightIcon={<Icon name="close" size={9} />}
                  onClick={() => setZones(zs => zs.filter((_, idx) => idx !== i))}
                >
                  {z.city}{z.state ? `, ${z.state}` : ''}
                </Badge>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Input
              type="text"
              size="sm"
              value={zoneInput}
              onChange={v => setZoneInput(v)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addZone(); } }}
              placeholder={t('settings.scrapeSchedule.zonePlaceholder')}
            />
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={addZone}>
              {t('settings.scrapeSchedule.addZone')}
            </Button>
          </div>
        </div>
      </ModalRow>

      <ModalRow label={t('settings.scrapeSchedule.capLabel')} hint={t('settings.scrapeSchedule.capHint')}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Input
            type="number"
            size="sm"
            min={1}
            max={50}
            value={String(capPerRun)}
            onChange={v => setCapPerRun(Math.min(50, Math.max(1, parseInt(v) || 1)))}
          />
          <span style={{ color: 'var(--evo-fg-4)', fontSize: '11px' }}>{t('settings.scrapeSchedule.timeLabel')}</span>
          <Dropdown<number> value={cronHour} options={hourOptions} onChange={setCronHour} size="sm" />
          <span style={{ color: 'var(--evo-fg-3)', fontSize: '13px' }}>:</span>
          <Dropdown<number> value={cronMinute} options={minuteOptions} onChange={setCronMinute} size="sm" />
        </div>
      </ModalRow>

      <ModalRow label={t('settings.scrapeSchedule.intentLabel')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {(['any', 'audit', 'demo'] as const).map(mode => (
            <Radio key={mode} value={mode} checked={intent === mode} onChange={() => setIntent(mode)}
              label={t(`settings.scrapeSchedule.intent.${mode}`)} />
          ))}
        </div>
      </ModalRow>

      {/* Automation steps */}
      <div style={{ marginBottom: '12px', padding: '12px', background: 'var(--evo-bg-2)', borderRadius: '6px' }}>
        <p style={{ fontFamily: 'var(--evo-font)', fontSize: '10px', fontWeight: 500, color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>
          {t('settings.pipelines.steps.title')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {steps.map(step => (
            <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', opacity: step.disabled ? 0.4 : 1 }}>
              <div>
                <p style={{ fontFamily: 'var(--evo-font)', fontSize: '13px', color: 'var(--evo-fg-2)', margin: 0 }}>
                  {t(`settings.pipelines.steps.${step.key}`)}
                </p>
                <p style={{ fontFamily: 'var(--evo-font)', fontSize: '11px', color: 'var(--evo-fg-4)', margin: '2px 0 0' }}>
                  {t(`settings.pipelines.steps.${STEP_HINT_KEY[step.key] ?? ''}`)}
                </p>
              </div>
              <Toggle
                checked={step.value}
                onChange={step.disabled ? (_v: boolean) => undefined : step.onChange}
                size="sm"
              />
            </div>
          ))}
        </div>
      </div>

      {error && (
        <p style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-danger)', marginBottom: '8px' }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
